import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Heart, Brain, MessageCircle, Shield, ChevronRight, Play, Star, Sparkles, Check, Download, Smartphone } from 'lucide-react';
import { motion } from 'framer-motion';
import CalvioLogo from '@/components/CalvioLogo';
import LanguageSelector from '@/components/LanguageSelector';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Store the deferred prompt globally so we don't miss it
let globalDeferredPrompt = null;

// Listen for beforeinstallprompt globally (before React mounts)
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    globalDeferredPrompt = e;
    console.log('Calvio: PWA install prompt captured');
  });
}

const Landing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState(globalDeferredPrompt);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Check if app is already installed
  useEffect(() => {
    // Check display mode
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    
    // Check for iOS standalone mode
    if (window.navigator.standalone === true) {
      setIsInstalled(true);
    }

    // Listen for future beforeinstallprompt events
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      globalDeferredPrompt = e;
      setDeferredPrompt(e);
      console.log('Calvio: PWA install prompt ready');
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      globalDeferredPrompt = null;
      console.log('Calvio: App installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // If we already have a global prompt, use it
    if (globalDeferredPrompt) {
      setDeferredPrompt(globalDeferredPrompt);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle install button click
  const handleInstallClick = useCallback(async () => {
    setIsInstalling(true);
    
    // If we have a deferred prompt, use it (native PWA install)
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          // Track installation
          try {
            await axios.post(`${BACKEND_URL}/api/track-install`, {
              platform: 'pwa',
              language: i18n.language,
              userAgent: navigator.userAgent,
              timestamp: new Date().toISOString()
            });
          } catch (error) {
            console.log('Install tracking failed:', error);
          }
        }
        setDeferredPrompt(null);
        globalDeferredPrompt = null;
      } catch (error) {
        console.log('Install prompt error:', error);
        setShowInstallModal(true);
      }
    } else {
      // Show install modal with instructions
      setShowInstallModal(true);
      
      // Track the click
      try {
        await axios.post(`${BACKEND_URL}/api/track-install`, {
          platform: navigator.userAgent.includes('Android') ? 'android_manual' : 
                    navigator.userAgent.includes('iPhone') ? 'ios_manual' : 'web_manual',
          language: i18n.language,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          type: 'manual_instructions'
        });
      } catch (error) {
        console.log('Install tracking failed:', error);
      }
    }
    
    setIsInstalling(false);
  }, [deferredPrompt, i18n.language]);

  // Detect platform for install instructions
  const getPlatformInstructions = () => {
    const ua = navigator.userAgent;
    if (ua.includes('Android')) {
      return {
        title: t('landing.install_title_android') || 'Install Calvio on Android',
        steps: [
          t('landing.install_step1_android') || '1. Tap the menu icon (⋮) in Chrome',
          t('landing.install_step2_android') || '2. Select "Add to Home Screen"',
          t('landing.install_step3_android') || '3. Tap "Add" to confirm'
        ],
        icon: '📱'
      };
    } else if (ua.includes('iPhone') || ua.includes('iPad')) {
      return {
        title: t('landing.install_title_ios') || 'Install Calvio on iPhone/iPad',
        steps: [
          t('landing.install_step1_ios') || '1. Tap the Share button (□↑)',
          t('landing.install_step2_ios') || '2. Scroll down and tap "Add to Home Screen"',
          t('landing.install_step3_ios') || '3. Tap "Add" to confirm'
        ],
        icon: '🍎'
      };
    } else {
      return {
        title: t('landing.install_title_desktop') || 'Install Calvio on Desktop',
        steps: [
          t('landing.install_step1_desktop') || '1. Look for the install icon (⊕) in the address bar',
          t('landing.install_step2_desktop') || '2. Click "Install"',
          t('landing.install_step3_desktop') || '3. The app will open in its own window'
        ],
        icon: '💻'
      };
    }
  };

  // Memoize features array to prevent re-creation on each render
  const features = useMemo(() => [
    {
      icon: Heart,
      titleKey: 'landing.feature_mood',
      descKey: 'landing.feature_mood_desc',
      color: 'bg-[#FF9F88]',
      path: '/mood'
    },
    {
      icon: Brain,
      titleKey: 'landing.feature_mindfulness',
      descKey: 'landing.feature_mindfulness_desc',
      color: 'bg-[#76A686]',
      path: '/exercises'
    },
    {
      icon: MessageCircle,
      titleKey: 'landing.feature_ai',
      descKey: 'landing.feature_ai_desc',
      color: 'bg-[#DCD6F7]',
      path: '/chat'
    },
    {
      icon: Shield,
      titleKey: 'landing.feature_crisis',
      descKey: 'landing.feature_crisis_desc',
      color: 'bg-[#BAE6FD]',
      path: '/crisis'
    }
  ], []);

  const scrollToFeatures = useCallback(() => {
    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleFeatureClick = useCallback((path) => {
    navigate('/login', { state: { redirectTo: path } });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 gradient-warm opacity-50" />
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1761623135057-e41b632694f2?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        {/* Navigation */}
        <nav className="relative z-50 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <CalvioLogo size={28} className="flex-shrink-0 sm:w-[52px] sm:h-[52px]" />
              <span className="text-lg sm:text-2xl font-serif font-semibold text-[#2D3748] truncate">Calvio</span>
            </div>
            <div className="flex items-center gap-3 sm:gap-3">
              <LanguageSelector 
                className="w-[72px] sm:w-32 bg-white border-stone-200 text-xs sm:text-sm h-8 sm:h-9 shadow-sm rounded-lg" 
              />
              <Button 
                onClick={() => navigate('/login')}
                className="btn-primary text-xs sm:text-base px-3 sm:px-4 py-1.5 sm:py-2 h-8 sm:h-9 shadow-sm whitespace-nowrap rounded-lg"
                data-testid="hero-get-started-btn"
              >
                {t('common.signup')}
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 md:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            {/* FREE Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-6 shadow-lg"
            >
              <Sparkles className="w-4 h-4" />
              {t('landing.free_badge')}
              <Sparkles className="w-4 h-4" />
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-[#2D3748] leading-tight mb-6">
              {t('landing.hero_title')}
            </h1>
            <div 
              className="mb-6 rounded-xl px-4 py-4 sm:px-6 sm:py-5 max-w-2xl mx-auto shadow-md border border-stone-200"
              style={{ backgroundColor: '#ffffff' }}
            >
              <p 
                className="text-base sm:text-lg md:text-xl leading-relaxed font-semibold"
                style={{ color: '#1a202c' }}
              >
                {t('landing.hero_subtitle')}
              </p>
            </div>
            
            {/* Free Features List */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8">
              {['free_feature_1', 'free_feature_2', 'free_feature_3'].map((key, index) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-bold px-3 py-2 rounded-full shadow-sm border border-stone-200"
                  style={{ backgroundColor: '#ffffff', color: '#1a202c' }}
                >
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#76A686]" />
                  {t(`landing.${key}`)}
                </motion.div>
              ))}
            </div>
            
            <p 
              className="text-sm sm:text-base font-bold mb-8 sm:mb-10"
              style={{ color: '#1a202c' }}
            >
              ✨ {t('landing.cta_primary')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button 
                onClick={() => navigate('/login')}
                className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-5 rounded-full font-bold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                style={{ backgroundColor: '#76A686', color: '#ffffff' }}
                data-testid="hero-start-journey-btn"
              >
                {t('landing.start_free')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-5 rounded-full font-bold shadow-md transition-all duration-300 border-2"
                style={{ backgroundColor: '#ffffff', color: '#76A686', borderColor: '#76A686' }}
                data-testid="hero-learn-more-btn"
                onClick={scrollToFeatures}
              >
                <Play className="w-5 h-5 mr-2" />
                {t('landing.cta_secondary')}
              </Button>
            </div>
            
            {/* Install App Button - Show only if not installed */}
            {!isInstalled && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-4"
              >
                <Button 
                  onClick={handleInstallClick}
                  disabled={isInstalling}
                  className="text-sm sm:text-base px-5 sm:px-6 py-3 sm:py-4 rounded-full font-bold shadow-md transition-all duration-300 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                  data-testid="hero-install-btn"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  {isInstalling ? t('landing.installing') : (deferredPrompt ? t('landing.install_app') : t('landing.install_app'))}
                </Button>
              </motion.div>
            )}
            
            {/* No credit card note */}
            <div 
              className="mt-5 px-4 py-2 rounded-full inline-block shadow-sm border border-stone-200"
              style={{ backgroundColor: '#ffffff' }}
            >
              <p 
                className="text-xs sm:text-sm font-semibold"
                style={{ color: '#1a202c' }}
              >
                {t('landing.no_credit_card')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className="py-12 sm:py-20 md:py-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-[#2D3748] mb-3 sm:mb-4">
            {t('landing.features_title')}
          </h2>
          <p className="text-base sm:text-lg text-[#718096] max-w-2xl mx-auto px-4">
            {t('landing.hero_subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.titleKey}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-base p-5 sm:p-6 lg:p-8 card-interactive"
              onClick={() => handleFeatureClick(feature.path)}
              data-testid={`feature-${t(feature.titleKey).toLowerCase().replace(' ', '-')}`}
            >
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-4 sm:mb-6`}>
                <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-serif font-semibold text-[#2D3748] mb-2 sm:mb-3">
                {t(feature.titleKey)}
              </h3>
              <p className="text-sm sm:text-base text-[#718096] leading-relaxed">
                {t(feature.descKey)}
              </p>
              <div className="mt-3 sm:mt-4 text-[#76A686] text-sm font-medium flex items-center gap-1">
                {t('landing.cta_primary')} <ChevronRight className="w-4 h-4" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 sm:py-16 gradient-calm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            {[
              { value: '2-5', labelKey: 'landing.stats_minutes' },
              { value: '24/7', labelKey: 'landing.stats_ai_support' },
              { value: '6+', labelKey: 'landing.stats_cbt_tasks' },
              { value: '100%', labelKey: 'landing.stats_private' }
            ].map((stat, index) => (
              <motion.div
                key={stat.labelKey}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-[#76A686] mb-1 sm:mb-2">
                  {stat.value}
                </div>
                <div className="text-[#718096] text-xs sm:text-sm md:text-base">
                  {t(stat.labelKey)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-[#FFFBF5]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-[#2D3748] mb-3 sm:mb-4">
              {t('landing.testimonials_title')}
            </h2>
            <p className="text-[#718096] text-base sm:text-lg max-w-2xl mx-auto px-4">
              {t('landing.testimonials_subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[1, 2, 3].map((num, index) => (
              <motion.div
                key={num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-sm border border-stone-100 relative"
              >
                {/* Quote mark */}
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 text-4xl sm:text-6xl text-[#76A686]/10 font-serif">&ldquo;</div>
                
                {/* Stars */}
                <div className="flex gap-1 mb-3 sm:mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                
                {/* Testimonial text */}
                <p className="text-sm sm:text-base text-[#4A5568] mb-4 sm:mb-6 relative z-10 leading-relaxed">
                  &ldquo;{t(`landing.testimonial_${num}_text`)}&rdquo;
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#76A686] to-[#5a8a6a] flex items-center justify-center text-white font-semibold text-base sm:text-lg">
                    {t(`landing.testimonial_${num}_name`).charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm sm:text-base text-[#2D3748]">
                      {t(`landing.testimonial_${num}_name`)}
                    </p>
                    <p className="text-xs sm:text-sm text-[#718096]">
                      {t(`landing.testimonial_${num}_role`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-[#2D3748] mb-3 sm:mb-4">
              {t('landing.faq_title')}
            </h2>
            <p className="text-[#718096] text-base sm:text-lg max-w-2xl mx-auto px-4">
              {t('landing.faq_subtitle')}
            </p>
          </motion.div>
          
          <div className="space-y-3 sm:space-y-4">
            {[
              { q: 'faq_q1', a: 'faq_a1' },
              { q: 'faq_q2', a: 'faq_a2' },
              { q: 'faq_q3', a: 'faq_a3' },
              { q: 'faq_q4', a: 'faq_a4' },
              { q: 'faq_q5', a: 'faq_a5' },
              { q: 'faq_q6', a: 'faq_a6' },
              { q: 'faq_q7', a: 'faq_a7' },
              { q: 'faq_q8', a: 'faq_a8' }
            ].map((item, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <summary className="flex items-center justify-between p-5 sm:p-6 cursor-pointer list-none">
                  <span className="font-semibold text-[#2D3748] pr-4 text-sm sm:text-base">
                    {t(`landing.${item.q}`)}
                  </span>
                  <ChevronRight className="w-5 h-5 text-[#76A686] transition-transform group-open:rotate-90 flex-shrink-0" />
                </summary>
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-[#4A5568] text-sm sm:text-base leading-relaxed border-t border-stone-100 pt-4">
                  {t(`landing.${item.a}`)}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="card-base p-8 sm:p-12 md:p-16 gradient-sage text-white relative overflow-hidden rounded-2xl sm:rounded-3xl">
            <div className="absolute top-0 right-0 w-32 sm:w-64 h-32 sm:h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 sm:w-48 h-24 sm:h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold mb-3 sm:mb-4">
                {t('landing.cta_title')}
              </h2>
              <p className="text-white/80 text-sm sm:text-base md:text-lg mb-6 sm:mb-8 max-w-xl mx-auto">
                {t('landing.cta_subtitle')}
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-white text-[#76A686] hover:bg-white/90 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg rounded-full font-medium transition-all duration-300 hover:-translate-y-0.5"
                data-testid="cta-get-started-btn"
              >
                {t('landing.cta_button')}
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 border-t border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="flex items-center gap-2">
              <CalvioLogo size={28} className="sm:w-8 sm:h-8" />
              <span className="text-base sm:text-lg font-serif font-medium text-[#2D3748]">Calvio</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
              <Link to="/blog" className="text-[#718096] hover:text-[#2D3748] transition-colors">
                {t('footer.blog')}
              </Link>
              <Link to="/privacy" className="text-[#718096] hover:text-[#2D3748] transition-colors">
                {t('footer.privacy_policy')}
              </Link>
              <Link to="/terms" className="text-[#718096] hover:text-[#2D3748] transition-colors">
                {t('footer.terms')}
              </Link>
              <Link to="/cookies" className="text-[#718096] hover:text-[#2D3748] transition-colors">
                {t('footer.cookies')}
              </Link>
              <Link to="/login" className="text-[#718096] hover:text-[#2D3748] transition-colors">
                {t('common.login')}
              </Link>
            </div>
          </div>
          
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-stone-100 text-center">
            <p className="text-[#A0AEC0] text-xs sm:text-sm">
              {t('footer.copyright')}
            </p>
            <p className="text-[#A0AEC0] text-xs mt-1">
              {t('footer.company')} (NEQ: 2270938857)
            </p>
          </div>
        </div>
      </footer>

      {/* Install Instructions Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowInstallModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <span className="text-4xl">{getPlatformInstructions().icon}</span>
              <h3 className="text-xl font-serif font-semibold text-[#2D3748] mt-2">
                {getPlatformInstructions().title}
              </h3>
            </div>
            
            <div className="space-y-3 mb-6">
              {getPlatformInstructions().steps.map((step, index) => (
                <div key={index} className="flex items-start gap-3 text-sm text-[#4A5568]">
                  <div className="w-6 h-6 rounded-full bg-[#76A686] text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    {index + 1}
                  </div>
                  <p className="pt-0.5">{step}</p>
                </div>
              ))}
            </div>
            
            <Button
              onClick={() => setShowInstallModal(false)}
              className="w-full bg-[#76A686] hover:bg-[#5a8a6a] text-white rounded-full py-3"
            >
              {t('common.close') || 'Got it!'}
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Landing;
